import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import moment from 'moment'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import LinearProgress from '@mui/material/LinearProgress'
import Paper from '@mui/material/Paper'
import Snackbar from '@mui/material/Snackbar'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import ArrowBackIos from '@mui/icons-material/ArrowBackIos'
import ArrowForwardIos from '@mui/icons-material/ArrowForwardIos'
import Refresh from '@mui/icons-material/Refresh'
import ViewList from '@mui/icons-material/ViewList'
import WarningAmber from '@mui/icons-material/WarningAmber'
import Accessible from '@mui/icons-material/Accessible'
import AirlineSeatFlat from '@mui/icons-material/AirlineSeatFlat'
import DirectionsWalk from '@mui/icons-material/DirectionsWalk'
import API from '../../../services/API'
import {
  QuoteStatusChip,
  useBookingQuoteStatuses,
} from '../components/quoteStatus'
import { describeOutstanding } from '../outstandingLabels'

const LEFT_RAIL_WIDTH = 200
const ROW_HEIGHT = 64
const HOUR_PX = { 1: 56, 3: 22, 7: 10 }

const DUTY_STATUS_COLORS = {
  default: { bg: '#E2E8F0', border: '#94A3B8', text: '#0F172A' },
  active: { bg: '#DBEAFE', border: '#3B82F6', text: '#1E3A8A' },
  complete: { bg: '#DCFCE7', border: '#22C55E', text: '#14532D' },
  problem: { bg: '#FEE2E2', border: '#EF4444', text: '#7F1D1D' },
}

const getDutyColors = (duty) => {
  if (['Completed'].includes(duty.status) || duty.dutyClosed) {
    return DUTY_STATUS_COLORS.complete
  }
  if (['Unavailable', 'Cancelled'].includes(duty.status)) {
    return DUTY_STATUS_COLORS.problem
  }
  if (['Off Duty', 'Planned', 'Crew Logging On'].includes(duty.status)) {
    return DUTY_STATUS_COLORS.default
  }
  return DUTY_STATUS_COLORS.active
}

const MOBILITY_ICONS = {
  walking: <DirectionsWalk sx={{ fontSize: 13 }} />,
  wheelchair: <Accessible sx={{ fontSize: 13 }} />,
  stretcher: <AirlineSeatFlat sx={{ fontSize: 13 }} />,
}

const capacityLine = (capacity = {}) =>
  ['walking', 'wheelchair', 'stretcher']
    .map((key) =>
      capacity[key] !== undefined && capacity[key] !== null
        ? `${capacity[key]}${key === 'walking' ? 'W' : key === 'wheelchair' ? 'C' : 'S'}`
        : null,
    )
    .filter(Boolean)
    .join(' · ')

// Positions a block within the visible window as percentage offsets
const blockPosition = (windowStart, windowEnd, start, end) => {
  const total = windowEnd - windowStart
  const from = Math.max(0, new Date(start) - windowStart)
  const to = Math.min(total, new Date(end) - windowStart)
  if (to <= 0 || from >= total) return null
  return {
    left: `${(from / total) * 100}%`,
    width: `${Math.max(0.5, ((to - from) / total) * 100)}%`,
  }
}

const DutyBlock = ({ duty, position, isDropTarget, onClick }) => {
  const colors = getDutyColors(duty)
  return (
    <Box
      onClick={onClick}
      sx={{
        position: 'absolute',
        top: 8,
        height: ROW_HEIGHT - 16,
        ...position,
        backgroundColor: isDropTarget ? '#FEF9C3' : colors.bg,
        border: `2px solid ${isDropTarget ? '#EAB308' : colors.border}`,
        borderRadius: 1.5,
        px: 0.75,
        py: 0.25,
        overflow: 'hidden',
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        minWidth: 40,
        '&:hover': { boxShadow: 2 },
      }}
    >
      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
        <Typography
          noWrap
          sx={{ fontSize: '0.72rem', fontWeight: 800, color: colors.text }}
        >
          {moment(duty.start).format('HH:mm')}–{moment(duty.end).format('HH:mm')}
        </Typography>
        {duty.warningCount > 0 && (
          <WarningAmber sx={{ fontSize: 14, color: '#B45309' }} />
        )}
      </Stack>
      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
        {['walking', 'wheelchair', 'stretcher'].map((key) =>
          duty.mobilityCounts?.[key] ? (
            <Stack
              key={key}
              direction="row"
              spacing={0.1}
              alignItems="center"
              sx={{ color: colors.text }}
            >
              {MOBILITY_ICONS[key]}
              <Typography sx={{ fontSize: '0.68rem', fontWeight: 700 }}>
                {duty.mobilityCounts[key]}
              </Typography>
            </Stack>
          ) : null,
        )}
        {duty.journeyCount === 0 && (
          <Typography sx={{ fontSize: '0.68rem', color: colors.text, opacity: 0.75 }}>
            No journeys
          </Typography>
        )}
      </Stack>
    </Box>
  )
}

const DeploymentBlock = ({ deployment, position }) => (
  <Tooltip title={deployment.label}>
    <Box
      sx={{
        position: 'absolute',
        top: 8,
        height: ROW_HEIGHT - 16,
        ...position,
        borderRadius: 1.5,
        border: '1px dashed #94A3B8',
        backgroundImage:
          'repeating-linear-gradient(45deg, rgba(148,163,184,0.25) 0 6px, rgba(148,163,184,0.08) 6px 12px)',
        display: 'flex',
        alignItems: 'center',
        px: 0.75,
        overflow: 'hidden',
      }}
    >
      <Typography noWrap sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#475569' }}>
        {deployment.label}
      </Typography>
    </Box>
  </Tooltip>
)

const TrayJourneyCard = ({ journey, onDragStart, onDragEnd, quoteStatus }) => (
  <Paper
    variant="outlined"
    draggable
    onDragStart={(event) => {
      // Firefox refuses to start a drag unless dragstart puts something in
      // dataTransfer; it also serves as a fallback for the drop handler.
      event.dataTransfer.setData('text/plain', journey._id)
      event.dataTransfer.effectAllowed = 'move'
      onDragStart(event)
    }}
    onDragEnd={onDragEnd}
    sx={{
      p: 1,
      minWidth: 190,
      maxWidth: 230,
      cursor: 'grab',
      borderColor: journey.overdue ? 'error.main' : 'divider',
      flexShrink: 0,
      '&:active': { cursor: 'grabbing' },
    }}
  >
    <Stack spacing={0.4}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography sx={{ fontWeight: 800, fontSize: '0.8rem' }} noWrap>
          {journey.patientName}
        </Typography>
        <Stack direction="row" spacing={0.25} alignItems="center">
          <QuoteStatusChip status={quoteStatus} warnOnly sx={{ height: 16 }} />
          {MOBILITY_ICONS[journey.mobility]}
          {journey.isBariatric && (
            <Chip size="small" label="B" color="warning" sx={{ height: 16, fontSize: '0.6rem', borderRadius: 0.5 }} />
          )}
        </Stack>
      </Stack>
      <Typography sx={{ fontSize: '0.7rem' }} color="text.secondary" noWrap>
        {[journey.pickupName, journey.dropoffName].filter(Boolean).join(' → ') ||
          journey.journeyType ||
          'Journey'}
      </Typography>
      <Stack direction="row" spacing={0.5} alignItems="center">
        <Typography
          sx={{ fontSize: '0.72rem', fontWeight: 700 }}
          color={journey.overdue ? 'error' : 'text.primary'}
        >
          {journey.anchorTime
            ? moment(journey.anchorTime).format('ddd DD/MM HH:mm')
            : 'No time set'}
        </Typography>
        {journey.overdue && (
          <Chip size="small" color="error" label="Overdue" sx={{ height: 16, fontSize: '0.6rem', borderRadius: 0.5 }} />
        )}
        {journey.escorts > 0 && (
          <Typography sx={{ fontSize: '0.68rem' }} color="text.secondary">
            +{journey.escorts} escort{journey.escorts === 1 ? '' : 's'}
          </Typography>
        )}
      </Stack>
    </Stack>
  </Paper>
)

const PlanningTimeline = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const initialDate = searchParams.get('date')
  const [startDate, setStartDate] = useState(
    initialDate && moment(initialDate, 'YYYYMMDD', true).isValid()
      ? moment(initialDate, 'YYYYMMDD')
      : moment(),
  )
  const [days, setDays] = useState(1)
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [draggingJourney, setDraggingJourney] = useState(null)
  const [dropTargetDuty, setDropTargetDuty] = useState(null)
  const [confirmAssign, setConfirmAssign] = useState(null) // { journey, duty }
  const [assigning, setAssigning] = useState(false)
  const [assignError, setAssignError] = useState(null)
  const [toast, setToast] = useState(null)
  const [selectedDuty, setSelectedDuty] = useState(null)

  const windowStart = useMemo(
    () => moment(startDate).startOf('day').toDate(),
    [startDate],
  )
  const windowEnd = useMemo(
    () => moment(startDate).add(days - 1, 'days').endOf('day').toDate(),
    [startDate, days],
  )

  const loadTimeline = useCallback(() => {
    setLoading(true)
    setError(null)
    API.get(
      `/pts/vehicle-timeline?start=${moment(windowStart).format('YYYY-MM-DD')}&end=${moment(windowEnd).format('YYYY-MM-DD')}`,
    )
      .then((res) => {
        if (res.status === 200) {
          setData(res.data)
        } else {
          setError(res.data?.error || 'Unable to load the timeline.')
        }
      })
      .catch((e) =>
        setError(e?.response?.data?.error || 'Unable to load the timeline.'),
      )
      .finally(() => setLoading(false))
  }, [windowStart, windowEnd])

  useEffect(() => {
    loadTimeline()
  }, [loadTimeline])

  const timelineWidth = days * 24 * HOUR_PX[days]

  // Quote position for every booking in view - flags unaccepted quotes
  const quoteStatusBookingIds = useMemo(() => {
    const ids = []
    ;(data?.duties || []).forEach((duty) =>
      (duty.routes || []).forEach((route) =>
        (route.journeys || []).forEach((journey) => ids.push(journey.bookingId)),
      ),
    )
    ;(data?.unassigned || []).forEach((journey) => ids.push(journey.bookingId))
    return ids
  }, [data])
  const quoteStatuses = useBookingQuoteStatuses(quoteStatusBookingIds)

  // One row per vehicle (grouped by base location) + one per vehicle-less duty
  const rows = useMemo(() => {
    if (!data) return []
    const dutiesByVehicle = new Map()
    const orphanDuties = []
    data.duties.forEach((duty) => {
      if (duty.vehicle?._id) {
        const key = String(duty.vehicle._id)
        if (!dutiesByVehicle.has(key)) dutiesByVehicle.set(key, [])
        dutiesByVehicle.get(key).push(duty)
      } else {
        orphanDuties.push(duty)
      }
    })

    const deploymentsByVehicle = new Map()
    data.deployments.forEach((deployment) => {
      const key = String(deployment.vehicle)
      if (!deploymentsByVehicle.has(key)) deploymentsByVehicle.set(key, [])
      deploymentsByVehicle.get(key).push(deployment)
    })

    const vehicleRows = [...data.vehicles]
      .sort((a, b) => {
        const groupCompare = (a.baseLocation?.name || 'zz').localeCompare(
          b.baseLocation?.name || 'zz',
        )
        if (groupCompare !== 0) return groupCompare
        return (a.callSign || '').localeCompare(b.callSign || '')
      })
      .map((vehicle) => ({
        type: 'vehicle',
        key: String(vehicle._id),
        vehicle,
        group: vehicle.baseLocation?.name || 'No base location',
        duties: dutiesByVehicle.get(String(vehicle._id)) || [],
        deployments: deploymentsByVehicle.get(String(vehicle._id)) || [],
      }))

    const orphanRows = orphanDuties.map((duty) => ({
      type: 'orphanDuty',
      key: `duty-${duty._id}`,
      duty,
      group: '⚠ No vehicle assigned',
      duties: [duty],
      deployments: [],
    }))

    return [...orphanRows, ...vehicleRows]
  }, [data])

  const groupedRows = useMemo(() => {
    const groups = []
    rows.forEach((row) => {
      const lastGroup = groups[groups.length - 1]
      if (lastGroup?.name === row.group) {
        lastGroup.rows.push(row)
      } else {
        groups.push({ name: row.group, rows: [row] })
      }
    })
    return groups
  }, [rows])

  // Journeys are dropped onto duty blocks, not bare vehicle rows — with no
  // duty visible in the window there is nothing to drop onto, and the drag
  // hint would just send people on a wild goose chase.
  const hasDutiesInView = useMemo(
    () =>
      rows.some((row) =>
        row.duties.some((duty) =>
          blockPosition(windowStart, windowEnd, duty.start, duty.end),
        ),
      ),
    [rows, windowStart, windowEnd],
  )

  const performAssign = async () => {
    if (!confirmAssign) return
    setAssigning(true)
    setAssignError(null)
    try {
      const res = await API.put(
        `/pts/journeys/${confirmAssign.journey._id}/assign`,
        { dutyId: confirmAssign.duty._id },
      )
      if (res.status === 200) {
        setConfirmAssign(null)
        const outstandingNote = res.data?.outstanding?.length
          ? ` Details still to confirm: ${describeOutstanding(res.data.outstanding)}.`
          : ''
        if (res.data?.warnings?.length || outstandingNote) {
          setToast({
            severity: 'warning',
            message: `Assigned with warnings: ${(res.data?.warnings || []).join(' ')}${outstandingNote}`,
          })
        } else {
          setToast({
            severity: 'success',
            message: `${confirmAssign.journey.patientName} assigned to ${confirmAssign.duty.vehicle?.callSign || 'duty'}.`,
          })
        }
        loadTimeline()
      } else if (res.data?.errorCode === 'capacityBlocked') {
        setAssignError(res.data)
      } else {
        setAssignError({ error: res.data?.error || 'Unable to assign journey.' })
      }
    } catch (e) {
      const payload = e?.response?.data
      setAssignError(
        payload?.errorCode === 'capacityBlocked'
          ? payload
          : { error: payload?.error || 'Unable to assign journey.' },
      )
    } finally {
      setAssigning(false)
    }
  }

  // The whole vehicle row is a drop zone, not just the duty block: resolve a
  // drag position to the duty covering that time, else the nearest duty on
  // the row - tiny blocks in the 3-day/week zooms are hopeless drop targets.
  const dutyAtDragPosition = (row, event) => {
    if (!row.duties.length) return null
    const rect = event.currentTarget.getBoundingClientRect()
    const fraction = (event.clientX - rect.left) / rect.width
    const time =
      windowStart.getTime() +
      fraction * (windowEnd.getTime() - windowStart.getTime())
    let best = null
    let bestDistance = Infinity
    row.duties.forEach((duty) => {
      const start = new Date(duty.start).getTime()
      const end = new Date(duty.end).getTime()
      const distance =
        time >= start && time <= end
          ? 0
          : Math.min(Math.abs(time - start), Math.abs(time - end))
      if (distance < bestDistance) {
        bestDistance = distance
        best = duty
      }
    })
    return best
  }

  const isJourneyDrag = (event) =>
    !!draggingJourney ||
    (event.dataTransfer?.types || []).includes('text/plain')

  const resolveDraggedJourney = (event) =>
    draggingJourney ||
    (data?.unassigned || []).find(
      (journey) => journey._id === event.dataTransfer?.getData('text/plain'),
    )

  const hourTicks = useMemo(() => {
    const ticks = []
    const stepHours = days === 1 ? 1 : days === 3 ? 3 : 6
    for (let hour = 0; hour < days * 24; hour += stepHours) {
      ticks.push(hour)
    }
    return ticks
  }, [days])

  const nowPosition = useMemo(() => {
    const now = new Date()
    if (now < windowStart || now > windowEnd) return null
    return `${((now - windowStart) / (windowEnd - windowStart)) * 100}%`
  }, [windowStart, windowEnd, data])

  return (
    <Stack spacing={1.5}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ md: 'center' }}
        spacing={1}
      >
        <Typography variant="title2">Vehicle Timeline</Typography>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <IconButton size="small" onClick={() => setStartDate((prev) => moment(prev).subtract(days, 'days'))}>
            <ArrowBackIos fontSize="small" />
          </IconButton>
          <TextField
            type="date"
            size="small"
            value={moment(startDate).format('YYYY-MM-DD')}
            onChange={(e) => {
              const next = moment(e.target.value, 'YYYY-MM-DD', true)
              if (next.isValid()) setStartDate(next)
            }}
          />
          <IconButton size="small" onClick={() => setStartDate((prev) => moment(prev).add(days, 'days'))}>
            <ArrowForwardIos fontSize="small" />
          </IconButton>
          <Button size="small" onClick={() => setStartDate(moment())}>
            Today
          </Button>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={days}
            onChange={(e, value) => value && setDays(value)}
          >
            <ToggleButton value={1}>Day</ToggleButton>
            <ToggleButton value={3}>3 Days</ToggleButton>
            <ToggleButton value={7}>Week</ToggleButton>
          </ToggleButtonGroup>
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={loadTimeline}>
              <Refresh fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button
            size="small"
            variant="outlined"
            startIcon={<ViewList />}
            onClick={() =>
              navigate(`/pts/planning?date=${moment(startDate).format('YYYYMMDD')}`)
            }
          >
            List View
          </Button>
        </Stack>
      </Stack>

      {data?.capacityEnforcement === 'block' && (
        <Alert severity="info" sx={{ py: 0.25 }}>
          Capacity enforcement is set to <b>block</b> — assignments that exceed
          vehicle capacity will be rejected.
        </Alert>
      )}
      {loading && <LinearProgress />}
      {error && (
        <Alert severity="error" variant="filled">
          {error}
        </Alert>
      )}

      {data && (
        <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
          <Box sx={{ display: 'flex', maxHeight: '58vh', overflow: 'auto' }}>
            {/* Left rail */}
            <Box
              sx={{
                width: LEFT_RAIL_WIDTH,
                flexShrink: 0,
                borderRight: '1px solid',
                borderColor: 'divider',
                position: 'sticky',
                left: 0,
                zIndex: 3,
                backgroundColor: 'background.paper',
              }}
            >
              <Box
                sx={{
                  height: 34,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  display: 'flex',
                  alignItems: 'center',
                  px: 1,
                  position: 'sticky',
                  top: 0,
                  backgroundColor: 'background.paper',
                  zIndex: 4,
                }}
              >
                <Typography variant="caption" fontWeight={800}>
                  Vehicle
                </Typography>
              </Box>
              {groupedRows.map((group) => (
                <Box key={group.name}>
                  <Box
                    sx={{
                      px: 1,
                      height: 25,
                      display: 'flex',
                      alignItems: 'center',
                      backgroundColor: 'action.hover',
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Typography variant="caption" fontWeight={800} color="text.secondary" noWrap>
                      {group.name}
                    </Typography>
                  </Box>
                  {group.rows.map((row) => (
                    <Box
                      key={row.key}
                      sx={{
                        height: ROW_HEIGHT,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        px: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        backgroundColor:
                          row.vehicle?.outOfService || row.vehicle?.openDefects > 0
                            ? 'rgba(239,68,68,0.06)'
                            : 'transparent',
                      }}
                    >
                      {row.type === 'vehicle' ? (
                        <>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <Typography sx={{ fontWeight: 800, fontSize: '0.85rem' }} noWrap>
                              {row.vehicle.callSign}
                            </Typography>
                            {row.vehicle.outOfService && (
                              <Chip size="small" color="error" label="VOR" sx={{ height: 18, fontSize: '0.62rem', borderRadius: 0.5 }} />
                            )}
                            {row.vehicle.openDefects > 0 && (
                              <Tooltip title={`${row.vehicle.openDefects} open defect${row.vehicle.openDefects === 1 ? '' : 's'}`}>
                                <Chip size="small" color="warning" label={`${row.vehicle.openDefects}⚠`} sx={{ height: 18, fontSize: '0.62rem', borderRadius: 0.5 }} />
                              </Tooltip>
                            )}
                          </Stack>
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {capacityLine(row.vehicle.ptsCapacity) || 'Capacity not set'}
                            {row.vehicle.ptsCapacity?.bariatric ? ' · Bariatric' : ''}
                            {row.vehicle.hasTailLift ? ' · Tail lift' : ''}
                          </Typography>
                        </>
                      ) : (
                        <>
                          <Typography sx={{ fontWeight: 800, fontSize: '0.85rem' }} color="warning.main" noWrap>
                            {row.duty.dutyRef || 'Duty'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap>
                            Needs a vehicle
                          </Typography>
                        </>
                      )}
                    </Box>
                  ))}
                </Box>
              ))}
            </Box>

            {/* Time grid */}
            <Box sx={{ position: 'relative', width: timelineWidth, flexShrink: 0 }}>
              {/* Hour scale */}
              <Box
                sx={{
                  height: 34,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  position: 'sticky',
                  top: 0,
                  backgroundColor: 'background.paper',
                  zIndex: 2,
                }}
              >
                {hourTicks.map((hour) => (
                  <Typography
                    key={hour}
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      position: 'absolute',
                      left: `${(hour / (days * 24)) * 100}%`,
                      top: 8,
                      pl: 0.5,
                      borderLeft: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    {days === 1
                      ? `${String(hour).padStart(2, '0')}:00`
                      : moment(windowStart).add(hour, 'hours').format(hour % 24 === 0 ? 'ddd DD' : 'HH:mm')}
                  </Typography>
                ))}
              </Box>

              {/* Grid lines + now marker */}
              {hourTicks.map((hour) => (
                <Box
                  key={`line-${hour}`}
                  sx={{
                    position: 'absolute',
                    top: 34,
                    bottom: 0,
                    left: `${(hour / (days * 24)) * 100}%`,
                    borderLeft: '1px solid',
                    borderColor: 'divider',
                    opacity: 0.5,
                  }}
                />
              ))}
              {nowPosition && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 34,
                    bottom: 0,
                    left: nowPosition,
                    borderLeft: '2px solid #EF4444',
                    zIndex: 1,
                  }}
                />
              )}

              {/* Rows */}
              {groupedRows.map((group) => (
                <Box key={group.name}>
                  <Box
                    sx={{
                      height: 25,
                      backgroundColor: 'action.hover',
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                    }}
                  />
                  {group.rows.map((row) => (
                    <Box
                      key={row.key}
                      onDragOver={(event) => {
                        if (!isJourneyDrag(event)) return
                        const duty = dutyAtDragPosition(row, event)
                        if (!duty) return
                        event.preventDefault()
                        event.dataTransfer.dropEffect = 'move'
                        setDropTargetDuty(duty._id)
                      }}
                      onDragLeave={(event) => {
                        if (event.currentTarget.contains(event.relatedTarget))
                          return
                        setDropTargetDuty(null)
                      }}
                      onDrop={(event) => {
                        event.preventDefault()
                        setDropTargetDuty(null)
                        const duty = dutyAtDragPosition(row, event)
                        const journey = resolveDraggedJourney(event)
                        if (duty && journey) {
                          setAssignError(null)
                          setConfirmAssign({ journey, duty })
                        }
                      }}
                      sx={{
                        position: 'relative',
                        height: ROW_HEIGHT,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        backgroundColor:
                          row.vehicle?.outOfService || row.vehicle?.openDefects > 0
                            ? 'rgba(239,68,68,0.04)'
                            : 'transparent',
                        backgroundImage: row.vehicle?.outOfService
                          ? 'repeating-linear-gradient(45deg, rgba(239,68,68,0.08) 0 8px, transparent 8px 16px)'
                          : 'none',
                      }}
                    >
                      {row.deployments.map((deployment) => {
                        const position = blockPosition(
                          windowStart,
                          windowEnd,
                          deployment.start,
                          deployment.end,
                        )
                        return position ? (
                          <DeploymentBlock
                            key={deployment._id}
                            deployment={deployment}
                            position={position}
                          />
                        ) : null
                      })}
                      {row.duties.map((duty) => {
                        const position = blockPosition(
                          windowStart,
                          windowEnd,
                          duty.start,
                          duty.end,
                        )
                        return position ? (
                          <DutyBlock
                            key={duty._id}
                            duty={duty}
                            position={position}
                            isDropTarget={dropTargetDuty === duty._id}
                            onClick={() => setSelectedDuty(duty)}
                          />
                        ) : null
                      })}
                    </Box>
                  ))}
                </Box>
              ))}
            </Box>
          </Box>
        </Paper>
      )}

      {/* Unplanned journey tray */}
      {data && (
        <Paper variant="outlined" sx={{ p: 1.5 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography fontWeight={800}>
              Journeys to plan ({data.unassigned.length})
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {hasDutiesInView
                ? "Drag a journey onto a duty block (the shaded bar on a vehicle's row) to assign it"
                : 'Journeys are assigned by dropping them onto a duty block'}
            </Typography>
          </Stack>
          {!hasDutiesInView && data.unassigned.length > 0 && (
            <Alert
              severity="warning"
              sx={{ mb: 1 }}
              action={
                <Button
                  size="small"
                  color="inherit"
                  onClick={() => navigate('/pts/planning')}
                >
                  Create duties
                </Button>
              }
            >
              There are no duties in this time window, so there is nothing to
              drop a journey onto yet. Create a duty (vehicle + crew) on the
              Planning page first — its block will then appear on the
              vehicle&apos;s row here.
            </Alert>
          )}
          {data.unassigned.length === 0 ? (
            <Alert severity="success" sx={{ py: 0.25 }}>
              Nothing waiting — every journey due in this window is planned.
            </Alert>
          ) : (
            <Stack
              direction="row"
              spacing={1}
              sx={{ overflowX: 'auto', pb: 0.5 }}
            >
              {data.unassigned.map((journey) => (
                <TrayJourneyCard
                  key={journey._id}
                  journey={journey}
                  quoteStatus={quoteStatuses[journey.bookingId]}
                  onDragStart={() => setDraggingJourney(journey)}
                  onDragEnd={() => {
                    setDraggingJourney(null)
                    setDropTargetDuty(null)
                  }}
                />
              ))}
            </Stack>
          )}
        </Paper>
      )}

      {/* Assign confirmation */}
      <Dialog
        open={!!confirmAssign}
        onClose={assigning ? null : () => setConfirmAssign(null)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Assign journey</DialogTitle>
        <DialogContent>
          <Stack spacing={1} pt={0.5}>
            <Typography variant="body2">
              Assign <b>{confirmAssign?.journey?.patientName}</b>
              {confirmAssign?.journey?.anchorTime
                ? ` (${moment(confirmAssign.journey.anchorTime).format('ddd DD/MM HH:mm')})`
                : ''}{' '}
              to{' '}
              <b>
                {confirmAssign?.duty?.vehicle?.callSign ||
                  confirmAssign?.duty?.dutyRef ||
                  'this duty'}
              </b>{' '}
              ({moment(confirmAssign?.duty?.start).format('HH:mm')}–
              {moment(confirmAssign?.duty?.end).format('HH:mm')})?
            </Typography>
            {assignError && (
              <Alert severity="error">
                {assignError.error}
                {(assignError.warnings || []).map((warning) => (
                  <Typography key={warning} variant="caption" display="block">
                    {warning}
                  </Typography>
                ))}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmAssign(null)}
            color="inherit"
            disabled={assigning}
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={performAssign} disabled={assigning}>
            {assigning ? 'Assigning...' : 'Assign'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Duty detail dialog */}
      <Dialog
        open={!!selectedDuty}
        onClose={() => setSelectedDuty(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontWeight: 800 }}>
          {selectedDuty?.vehicle?.callSign || selectedDuty?.dutyRef || 'Duty'} —{' '}
          {moment(selectedDuty?.start).format('ddd DD MMM HH:mm')}–
          {moment(selectedDuty?.end).format('HH:mm')}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5}>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              <Chip size="small" label={selectedDuty?.status} sx={{ borderRadius: 1, fontWeight: 700 }} />
              {!selectedDuty?.vehicle && (
                <Chip size="small" color="warning" label="No vehicle" sx={{ borderRadius: 1, fontWeight: 700 }} />
              )}
              {selectedDuty?.warningCount > 0 && (
                <Chip
                  size="small"
                  color="warning"
                  icon={<WarningAmber sx={{ fontSize: 14 }} />}
                  label={`${selectedDuty.warningCount} capacity warning${selectedDuty.warningCount === 1 ? '' : 's'}`}
                  sx={{ borderRadius: 1, fontWeight: 700 }}
                />
              )}
            </Stack>
            {(selectedDuty?.routes || []).map((route, routeIndex) => (
              <Box key={routeIndex}>
                <Typography variant="subtitle2" fontWeight={800}>
                  Route {routeIndex + 1}
                  {route.plannedStart
                    ? ` · ${moment(route.plannedStart).format('HH:mm')}${route.plannedEnd ? `–${moment(route.plannedEnd).format('HH:mm')}` : ''}`
                    : ''}
                </Typography>
                {route.warnings.map((warning) => (
                  <Alert key={warning} severity="warning" sx={{ py: 0, my: 0.5 }}>
                    {warning}
                  </Alert>
                ))}
                <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                  {route.journeys.map((journey) => (
                    <Stack
                      key={journey._id}
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
                        {MOBILITY_ICONS[journey.mobility]}
                        <Typography variant="body2" noWrap>
                          {journey.patientName}
                          {journey.bookingRef ? ` — ${journey.bookingRef}` : ''}
                        </Typography>
                      </Stack>
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <QuoteStatusChip
                          status={quoteStatuses[journey.bookingId]}
                          warnOnly
                        />
                        <Typography variant="caption" color="text.secondary">
                          {journey.anchorTime
                            ? moment(journey.anchorTime).format('HH:mm')
                            : ''}
                        </Typography>
                        <Chip size="small" label={journey.status} sx={{ borderRadius: 1, height: 20, fontSize: '0.65rem' }} />
                      </Stack>
                    </Stack>
                  ))}
                  {route.journeys.length === 0 && (
                    <Typography variant="caption" color="text.secondary">
                      No journeys on this route.
                    </Typography>
                  )}
                </Stack>
                <Divider sx={{ mt: 1 }} />
              </Box>
            ))}
            {(selectedDuty?.routes || []).length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No routes planned on this duty yet — drag a journey from the
                tray onto the vehicle&apos;s row.
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              navigate(
                `/pts/planning?date=${moment(selectedDuty?.start).format('YYYYMMDD')}`,
              )
            }
          >
            Open in Planning
          </Button>
          <Button onClick={() => setSelectedDuty(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={6000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          severity={toast?.severity || 'info'}
          variant="filled"
          onClose={() => setToast(null)}
        >
          {toast?.message}
        </Alert>
      </Snackbar>
    </Stack>
  )
}

export default PlanningTimeline
